import {
  Box,
  Text,
  Button,
  useDisclosure,
  Image
} from '@chakra-ui/react';
import {
  useState,
  useCallback,
  useMemo,
  useRef,
  FormEvent,
  useEffect
} from 'react';
import UserSearch from '../elements/UserSearch';
import { AgGridReact } from 'ag-grid-react';
import { ColDef, GridOptions, ValueFormatterParams } from 'ag-grid-community';
import { agGridIconsConfig } from '@/explore/ffl-detail/utils.mjs';
import { UserListInterface } from '@/model/userList.mjs';
import AddUserModal from '../elements/AddUserModal';
import { CheckIcon } from '@chakra-ui/icons';
import AppConfig from '@/utils/app-config.mjs';
import { client } from '@/api/backoffice-api';
import ConfirmationModal from '@/common/ConfirmationModal';
import { useDispatch } from 'react-redux';
import { callToDeleteUser, setIsUserDelete } from '../ducks/slice';
import { getIsDeletingUser, getIsUserDeleted } from '../ducks/selectors';
import { DELETE_USER_CONFIRMATION } from '@/utils/constants';
import EditUserModal from '../elements/EditUserModal';
import { analyticsEvents } from '@/utils/analyticsEvents';
import { logAnalyticsEvent } from '@/utils/analyticsLogger';
interface UserProps {
  doRefresh: number;
}

export default function ManageUsers({ doRefresh }: UserProps) {
  const { isOpen, onOpen, onClose } = useDisclosure();
  const { isOpen: isOpenEditUserModal, onOpen: onOpenEditUserModal, onClose: onCloseEditUserModal } = useDisclosure();
  const [rowData, setRowData] = useState();
  const [userSearchText, setUserSearchText] = useState('');
  const [userDetails, setuserDetails] = useState<UserListInterface | null>(null);
  const gridRef = useRef<AgGridReact<UserListInterface>>(null);
  const gridStyle = useMemo(() => ({ width: '100%' }), []);
  const dispatch = useDispatch();
  const statusView = ({ value }: { value: boolean }) => {
    return value ? (
      <div className='status-wrapper'>
        <CheckIcon
          color="#35B963"
          className='status-wrapper__icon status-wrapper__icon-chakra'
        />
        <span>Enabled</span>
      </div>
    ) : (
      <div className='status-wrapper'>
        <Image className='status-wrapper__icon' src='/images/ui/disable.svg' />
        <span>Disabled</span>
      </div>
    );
  };
  const isDeletingUser = getIsDeletingUser();
  const isUserDeleted = getIsUserDeleted();

  useEffect(() => {
    if (isUserDeleted) {
      dispatch(setIsUserDelete(false));
      onCloseDeleteConfirmPopup();
      onGridReady();
    }
  }, [isUserDeleted]);

  const ActionUserDetails = ({ data }: { data: UserListInterface }) => {
    const onClickRemove = (userName: string) => {
      return () => onClickDeleteUserHandler(userName);
    };
    const onClickEdit = () => {
      logAnalyticsEvent(analyticsEvents.editUser);
      setuserDetails(data);
      onOpenEditUserModal();
    };
    return (
      <>
        <Button className="ag-grid-action-btn action-btn" onClick={onClickRemove(data.email)}>
          <Image src="/images/ui/delete.svg" />
        </Button>
        <Button className="ag-grid-action-btn action-btn" onClick={onClickEdit}>
          <Image src="/images/ui/edit-dark.svg" />
        </Button>
      </>
    );
  };
  const sortingData = (a: string | number, b: string | number) => {
    const valueA = typeof a === 'string' ? a.toLowerCase() : a;
    const valueB = typeof b === 'string' ? b.toLowerCase() : b;
    return valueA == valueB ? 0 : valueA > valueB ? 1 : -1;
  };
  useEffect(() => {
    if (doRefresh === 1) {
      onGridReady();
    }
  }, [doRefresh]);
  const columnDefs: ColDef[] = [
    {
      field: 'firstName',
      headerName: 'First Name',
      width: 250,
      comparator: sortingData,
      valueFormatter: function ({ data }: ValueFormatterParams) {
        const firstName = data?.firstName || null;
        return firstName
          ? firstName.charAt(0).toUpperCase() + firstName.slice(1)
          : null;
      }
    },
    {
      field: 'lastName',
      headerName: 'Last Name',
      width: 200,
      comparator: sortingData,
      valueFormatter: function ({ data }: ValueFormatterParams) {
        const lastName = data?.lastName || null;
        return lastName
          ? lastName.charAt(0).toUpperCase() + lastName.slice(1)
          : null;
      }
    },
    {
        field: 'phone',
        headerName: 'Phone Number',
        width: 200,
        comparator: sortingData,
        valueFormatter: function ({ data }: ValueFormatterParams) {
          const phone = data?.phone || null;
          return phone
            ? phone.substring(phone.length - 10)
            : null;
        }
      },
    {
      field: 'email',
      headerName: 'Email Id',
      width: 300
    },
    {
      field: 'role',
      headerName: 'Role',
      width: 200,
      comparator: sortingData,
      getQuickFilterText: (params) => {
        return '';
      },
      valueFormatter: function ({ data }: ValueFormatterParams) {
        const role = data?.role || null;
        return role ? role.charAt(0).toUpperCase() + role.slice(1) : null;
      }
    },
    {
      field: 'transferCount',
      headerName: 'Transfer',
      width: 250,
      comparator: sortingData,
      getQuickFilterText: (params) => {
        return '';
      }
    },
    {
      field: 'status',
      headerName: 'Account Status',
      width: 230,
      cellRenderer: statusView,
      getQuickFilterText: (params) => {
        return '';
      }
    },
    {
      headerName: 'Actions',
      width: 150,
      cellRenderer: ActionUserDetails,
      autoHeight: true
    }
  ];

  const defaultColDef = useMemo(
    () => ({
      resizable: true,
      sortable: true
    }),
    []
  );
  const onGridReady = useCallback(async () => {
    const response = await client.get(
      `${AppConfig.fflExpressServiceApiURL}/backend/stores/users`
    );
    setRowData(response.data);
  }, []);

  const gridOptions: GridOptions<UserListInterface> = {
    pagination: false,
    serverSideEnableClientSideSort: true,
    // column definitions
    columnDefs,
    defaultColDef,

    // row options
    animateRows: true,
    suppressRowClickSelection: true,

    // cell detail options
    suppressCellFocus: true,

    // misc options
    debug: false,
    icons: agGridIconsConfig,
    overlayNoRowsTemplate:
      '<div class="not-found-box"><img src="/images/ui/not-found-icon.png"></img><p class="not-found-box__title">No User found</p><p class="not-found-box__message">Try adjusting your search or filter to find what you’re looking for</p></div>'
  };
  const handleSearch = (event: FormEvent) => {
    event.preventDefault();
    console.log('Search changes', userSearchText);
    gridRef.current?.api.setGridOption('quickFilterText', userSearchText);
  };

  function reFetchData() {
    onGridReady();
  }

  const {
    isOpen: isDeleteConfirmPopupOpen,
    onClose: onCloseDeleteConfirmPopup,
    onOpen: onOpenDeleteConfirmPopup
  } = useDisclosure();
  const [userToDelete, setUserToDelete] = useState('');

  const onDeleteUserHandler = () => {
    logAnalyticsEvent(analyticsEvents.deleteUser);
    dispatch(callToDeleteUser(userToDelete));
  };

  const onClickDeleteUserHandler = (userName: string) => {
    onOpenDeleteConfirmPopup();
    setUserToDelete(userName);
  };


  return (
    <Box className={'Queue'}>
      {isDeleteConfirmPopupOpen && (
        <ConfirmationModal
          isOpen={isDeleteConfirmPopupOpen}
          modalHeader={'Please confirm'}
          onClose={onCloseDeleteConfirmPopup}
          isCancelBtnDisable={isDeletingUser}
          isConfirmBtnDisable={isDeletingUser}
          isConfirmBtnLoading={isDeletingUser}
          onClickConfirmBtn={onDeleteUserHandler}
          modalContent={
            <>
              {
                DELETE_USER_CONFIRMATION
              }{' '}
            </>
          }
        />
      )}
      <Box className="transfer-header">
        <UserSearch
          userSearchText={userSearchText}
          setUserSearchText={setUserSearchText}
          handleSearch={handleSearch}
        />
        <div className="transfer-header__right">
          <Box className="transfer-filters">
            <Button
              colorScheme="blue"
              background="#0C1E46"
              padding="8px 20px"
              onClick={() => {
                logAnalyticsEvent(analyticsEvents.addUser);
                onOpen();
                reFetchData();
              }}
            >
              + Add User
            </Button>
          </Box>
        </div>
      </Box>
      <div style={gridStyle} className='grid-height'>
        <AgGridReact
          className={'ag-theme-alpine'}
          ref={gridRef}
          rowData={rowData}
          gridOptions={gridOptions}
          onGridReady={onGridReady}
        />
      </div>
      {isOpen && (
        <AddUserModal
          reFetchData={reFetchData}
          isOpen={isOpen}
          onClose={onClose}
        />
      )}
      {isOpenEditUserModal && (
        <EditUserModal
          reFetchData={reFetchData}
          isOpen={isOpenEditUserModal}
          onClose={onCloseEditUserModal}
          userDetails={userDetails}
        />
      )}
    </Box>
  );
}
